import {
  addRebarToCurrentOrder,
  setDefaultRebars,
  setRebarFilters,
  setRebarPage,
  updateCurrentOrderRebar,
  removeCurrentOrderRebar,
} from "Redux/features/rebar";
import { rebarStore } from "Redux/offline";
import { AppThunk } from "Redux/store";
import { isAxiosResponse } from "Services/index";
import {
  postOrderItem,
  updateOrderItem,
  updateRebarSketch,
  getItemsCount,
} from "Services/order";
import { getDefaultRebars, getFilters } from "Services/rebar";
import { REBAR_SUBCLASS_TYPE } from "Constants/rebar-constants";
import { getMedia } from "Services/media";

/*
 * Serializes rebar creation requests in this browser session.
 * This prevents two fast submissions from reading the same last row number
 * and both being created with the same row_number.
 *
 * The backend should still enforce row numbering atomically as the final
 * source of truth, but this removes the frontend race condition.
 */
let createRebarQueue: Promise<unknown> = Promise.resolve();

const enqueueRebarCreation = <T>(task: () => Promise<T>): Promise<T> => {
  const queuedTask = createRebarQueue.then(task, task);

  // Keep the queue usable even when one task fails.
  createRebarQueue = queuedTask.then(
    () => undefined,
    () => undefined
  );

  return queuedTask;
};

// NOTE: row_number is 0 when creating a new item and > 0 when duplicating.
export const createRebar =
  (
    rebar: any,
    orderItem: any,
    next = (val: boolean) => {},
    rowNum = 0,
    orderId = 0
  ): AppThunk =>
  async (dispatch, getState) => {
    return enqueueRebarCreation(async () => {
      const user = getState().user.userData;

      if (!user) {
        const rebarData = {
          ...orderItem,
          tool: rebar,
        };

        const previewID = rebarData.tool?.preview_image;

        if (!previewID) {
          throw new Error("לא התקבל מזהה לתמונה המקדימה");
        }

        const resMe = await getMedia(previewID);

        if (!isAxiosResponse(resMe)) {
          throw new Error("טעינת התמונה המקדימה נכשלה");
        }

        const orderIt = {
          ...rebarData,
          tool: {
            ...rebarData.tool,
            preview_image: { ...resMe.data },
          },
        };

        await rebarStore.setItem(orderIt);
        await dispatch(addRebarToCurrentOrder(orderIt));

        next(true);
        return true;
      }

      const countResult = await getItemsCount(REBAR_SUBCLASS_TYPE, orderId);

      if (!isAxiosResponse(countResult)) {
        throw new Error("קבלת מספר הפריט הבא נכשלה");
      }

      const rowNumber = rowNum > 0 ? rowNum : countResult.data.row_number + 1;

      const payload = {
        ...orderItem,
        row_number: rowNumber,
        tool: rebar,
        user: user.id,
        ...(orderId > 0 ? { order: orderId } : {}),
      };

      const res = await postOrderItem(payload);

      if (!isAxiosResponse(res)) {
        throw new Error("יצירת פריט המוט נכשלה");
      }

      const previewID = res.data?.tool?.preview_image;

      if (!previewID) {
        throw new Error("לא התקבל מזהה לתמונה המקדימה");
      }

      const resMe = await getMedia(previewID);

      if (!isAxiosResponse(resMe)) {
        throw new Error("טעינת התמונה המקדימה נכשלה");
      }

      const orderIt = {
        ...res.data,
        tool: {
          ...res.data.tool,
          preview_image: { ...resMe.data },
        },
      };

      // Always add a successful server response to Redux.
      // The old conditional caused successfully-created items to appear only
      // after a page refresh when row numbers were equal or out of order.
      await dispatch(addRebarToCurrentOrder(orderIt));

      next(true);
      return true;
    });
  };

export const updateRebar =
  (
    orderItemID: number,
    rebar: any,
    orderItem: any,
    next = (val: boolean) => {}
  ): AppThunk =>
  async (dispatch, getState) => {
    const user = getState().user.userData;

    if (!user) {
      throw new Error("לא נמצא משתמש מחובר");
    }

    const res = await updateOrderItem(orderItemID, {
      ...orderItem,
      tool: rebar,
    });

    if (!isAxiosResponse(res)) {
      throw new Error("עדכון פריט המוט נכשל");
    }

    const previewID = res.data?.tool?.preview_image;

    if (!previewID) {
      throw new Error("לא התקבל מזהה לתמונה המקדימה");
    }

    const resMe = await getMedia(previewID);

    if (!isAxiosResponse(resMe)) {
      throw new Error("טעינת התמונה המקדימה נכשלה");
    }

    const orderIt = {
      ...res.data,
      tool: {
        ...res.data.tool,
        preview_image: { ...resMe.data },
      },
    };

    await dispatch(updateCurrentOrderRebar(orderIt));

    next(true);
    return true;
  };

export const fetchDefaultsRebars =
  (): AppThunk => async (dispatch, getState) => {
    const rebarPage = getState().rebar.page;

    if (rebarPage || rebarPage === "") {
      const res = await getDefaultRebars(rebarPage);

      if (res.results.length > 0) {
        const tools = getState().rebar.defaultRebars;
        const page = res.next
          ? res.next.split("cursor=")[1].split("&")[0]
          : false;

        await dispatch(setRebarPage(page));
        await dispatch(
          setDefaultRebars([
            ...tools,
            ...res.results.sort((a: any, b: any) => a.id - b.id),
          ])
        );
      }
    } else {
      await dispatch(setRebarPage(""));
    }
  };

export const fetchRebarFilters = (): AppThunk => async (dispatch) => {
  const response = await getFilters();

  if (isAxiosResponse(response)) {
    await dispatch(setRebarFilters(response.data));
  }
};

export const changeRebarSketch =
  (rebarData: any, orderId: number, orderItem: any): AppThunk =>
  async (dispatch, getState) => {
    const user = getState().user.userData;

    if (!user) {
      throw new Error("לא נמצא משתמש מחובר");
    }

    const res = await updateRebarSketch(
      {
        ...orderItem,
        tool: rebarData,
        user: user.id,
      },
      orderId
    );

    if (!isAxiosResponse(res)) {
      throw new Error("שינוי שרטוט המוט נכשל");
    }

    const previewID = res.data?.tool?.preview_image;

    if (!previewID) {
      throw new Error("לא התקבל מזהה לתמונה המקדימה");
    }

    const resMe = await getMedia(previewID);

    if (!isAxiosResponse(resMe)) {
      throw new Error("טעינת התמונה המקדימה נכשלה");
    }

    const orderIt = {
      ...res.data,
      tool: {
        ...res.data.tool,
        preview_image: { ...resMe.data },
      },
    };

    await dispatch(removeCurrentOrderRebar(orderId));
    await dispatch(addRebarToCurrentOrder(orderIt));

    return true;
  };